/**
 * Manuscripts Scene: browse and read collected lore manuscripts.
 *
 * Accessible from the title menu. Lists all 30 manuscripts organized by
 * story arc. Collected manuscripts can be selected and read; uncollected
 * ones are shown as locked entries.
 *
 * Navigation:
 *   UP/DOWN — Select manuscript
 *   ENTER/SPACE — Read selected manuscript (if collected)
 *   ESC — Return to list (when reading) or title (when browsing)
 */

import Phaser from 'phaser';
import { SCENE_MANUSCRIPTS, SCENE_TITLE } from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  AUDIO_MENU_CLICK,
  AUDIO_MENU_SELECT,
  AUDIO_ESC_PRESSED,
  AUDIO_MANUSCRIPT_OPEN,
  AUDIO_LIBRARY_BG,
} from '../config/game-config.js';
import { getSfxVol, getMusicVol } from '../utils/audio-config.js';
import { refreshConfig, applyMasterMute } from '../utils/audio-config.js';
import { loadMusicPref } from '../utils/save-manager.js';
import { MANUSCRIPTS, MANUSCRIPT_COUNT, ARC_NAMES } from '../data/ManuscriptData.js';
import { getCollectedManuscripts, getCollectedCount, markManuscriptRead, getReadManuscripts } from '../utils/manuscript-manager.js';

// --- Layout ---
const CONTENT_LEFT = BORDER_MARGIN + 30;
const CX = GAME_WIDTH / 2;
const LIST_START_Y = 112;
const ROW_HEIGHT = 18;
const VISIBLE_ROWS = 22;         // max visible rows before scroll needed

// --- Text styles ---
const HEADER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '22px',
  color: '#ffffff',
  letterSpacing: 3,
};

const COUNTER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#999999',
};

const INTRO_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#aaaaaa',
  wordWrap: { width: 540 },
  lineSpacing: 3,
};

const ARC_TITLE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#d4b876',
};

const ITEM_COLLECTED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#bbbbbb',
};

const ITEM_LOCKED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#555555',
};

const ITEM_SELECTED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#ffffff',
};

const ITEM_NEW_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#f0d890',
};

const ITEM_NEW_SELECTED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#ffffff',
};

const NEW_TAG_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '9px',
  color: '#ffffff',
  backgroundColor: '#8b6914',
  padding: { x: 3, y: 1 },
};

const HINT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#777777',
};

// --- Reading view styles ---
const READ_TITLE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '18px',
  color: '#ffffff',
};

const READ_ARC_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#d4b876',
};

const READ_BODY_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#dddddd',
  wordWrap: { width: 510 },
  lineSpacing: 6,
};

const READ_HINT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#777777',
};

/**
 * Represents a row in the manuscripts list — either an arc header or a manuscript entry.
 * @typedef {object} ListRow
 * @property {'arc'|'manuscript'} type
 * @property {number} [arcNum] - For arc headers
 * @property {string} [arcName] - For arc headers
 * @property {number} [id] - Manuscript ID (for manuscript rows)
 * @property {string} [title] - Manuscript title
 * @property {boolean} [collected] - Whether the player has this manuscript
 * @property {boolean} [isNew] - Whether the manuscript is collected but not yet read
 */

export default class ManuscriptsScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_MANUSCRIPTS });
  }

  create() {
    refreshConfig();
    applyMasterMute(this.sound);

    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    this._transitioning = false;
    this._reading = false;

    // Start library background music if music is enabled
    this._libraryMusic = null;
    this._startMusic();

    // Build the flat list of rows (arc headers + manuscript entries)
    this._collected = getCollectedManuscripts();
    this._readSet = getReadManuscripts();
    this._buildRowList();

    // Cursor: index into _selectableIndices (only manuscript rows)
    this._selectableIndices = [];
    for (let i = 0; i < this._rows.length; i++) {
      if (this._rows[i].type === 'manuscript') {
        this._selectableIndices.push(i);
      }
    }
    this._cursorIdx = 0;  // index into _selectableIndices

    // Scroll state
    this._scrollOffset = 0;

    // Draw the list view
    this._drawBorder();
    this._drawListView();

    // Input
    this._setupInput();
  }

  // ---------------------------------------------------------------
  // Row List Builder
  // ---------------------------------------------------------------

  /** @private */
  _buildRowList() {
    /** @type {ListRow[]} */
    this._rows = [];
    let currentArc = 0;

    for (const m of MANUSCRIPTS) {
      if (m.arc !== currentArc) {
        currentArc = m.arc;
        this._rows.push({
          type: 'arc',
          arcNum: currentArc,
          arcName: ARC_NAMES[currentArc] || `Arc ${currentArc}`,
        });
      }
      this._rows.push({
        type: 'manuscript',
        id: m.id,
        title: m.title,
        collected: this._collected.has(m.id),
        isNew: this._collected.has(m.id) && !this._readSet.has(m.id),
      });
    }
  }

  // ---------------------------------------------------------------
  // List View
  // ---------------------------------------------------------------

  /** @private */
  _drawListView() {
    // Clean up any existing list container
    if (this._listContainer) {
      this._listContainer.destroy();
    }
    if (this._fixedUI) {
      this._fixedUI.destroy();
    }

    // Fixed UI elements (not scrolled)
    this._fixedUI = this.add.container(0, 0);

    // Header
    const header = this.add.text(CX, 40, 'MANUSCRIPTS', HEADER_STYLE).setOrigin(0.5);
    this._fixedUI.add(header);

    // Decorative line
    const lineG = this.add.graphics();
    lineG.lineStyle(1, COLOR_WHITE, 0.3);
    lineG.lineBetween(CX - 120, 60, CX + 120, 60);
    this._fixedUI.add(lineG);

    // Counter
    const count = getCollectedCount();
    const counterText = `${count} / ${MANUSCRIPT_COUNT} collected`;
    const counter = this.add.text(CX, 72, counterText, COUNTER_STYLE).setOrigin(0.5);
    this._fixedUI.add(counter);

    // Intro text
    const intro = this.add.text(CONTENT_LEFT, 87,
      'Ancient parchments from the cult of Threia, scattered across the labyrinth.',
      INTRO_STYLE);
    this._fixedUI.add(intro);

    // Hints at bottom
    const hintText = this._selectableIndices.length > 0
      ? 'UP/DOWN select  |  ENTER read  |  ESC return'
      : 'No manuscripts collected yet  |  ESC return';
    const hint = this.add.text(CX, GAME_HEIGHT - BORDER_MARGIN - 12, hintText, HINT_STYLE)
      .setOrigin(0.5);
    this._fixedUI.add(hint);
    this.tweens.add({
      targets: hint,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
    });

    // Scroll hint (right side)
    if (this._rows.length > VISIBLE_ROWS) {
      const scrollHint = this.add.text(
        GAME_WIDTH - BORDER_MARGIN - 10,
        GAME_HEIGHT - BORDER_MARGIN - 12,
        'scroll',
        { ...HINT_STYLE, fontSize: '10px' },
      ).setOrigin(1, 0.5);
      this._fixedUI.add(scrollHint);
    }

    // Scrollable list container
    this._listContainer = this.add.container(0, 0);

    // Mask to clip list area (prevent scrolled content from overlapping header)
    const maskShape = this.make.graphics({ add: false });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(
      BORDER_MARGIN + 5, LIST_START_Y,
      GAME_WIDTH - (BORDER_MARGIN + 5) * 2,
      GAME_HEIGHT - LIST_START_Y - BORDER_MARGIN - 25,
    );
    this._listContainer.setMask(maskShape.createGeometryMask());

    // Render rows
    this._rowTexts = [];
    let y = LIST_START_Y;

    for (let i = 0; i < this._rows.length; i++) {
      const row = this._rows[i];

      if (row.type === 'arc') {
        // Arc header
        const arcLabel = this.add.text(
          CONTENT_LEFT,
          y,
          `-- ${row.arcName} --`,
          ARC_TITLE_STYLE,
        );
        this._listContainer.add(arcLabel);
        this._rowTexts.push({ text: arcLabel, type: 'arc' });
        y += ROW_HEIGHT + 2;
      } else {
        // Manuscript entry
        const romanNum = this._toRoman(row.id);
        const displayText = row.collected
          ? `  ${romanNum}. ${row.title}`
          : `  ${romanNum}. ????????`;

        // New (unread) manuscripts use a golden style to stand out
        let style;
        if (row.isNew) {
          style = ITEM_NEW_STYLE;
        } else if (row.collected) {
          style = ITEM_COLLECTED_STYLE;
        } else {
          style = ITEM_LOCKED_STYLE;
        }
        const label = this.add.text(CONTENT_LEFT, y, displayText, style);
        this._listContainer.add(label);

        // Mouse interaction for manuscript entries
        label.setInteractive({ useHandCursor: row.collected });
        label.on('pointerover', () => {
          if (this._reading || this._transitioning) return;
          // Find cursor index for this row
          const cursorPos = this._selectableIndices.indexOf(i);
          if (cursorPos >= 0 && this._cursorIdx !== cursorPos) {
            this._cursorIdx = cursorPos;
            try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.3) }); } catch (_) { /* */ }
            this._updateCursorHighlight();
          }
        });
        label.on('pointerdown', () => {
          if (this._reading || this._transitioning) return;
          const cursorPos = this._selectableIndices.indexOf(i);
          if (cursorPos >= 0) {
            this._cursorIdx = cursorPos;
            this._updateCursorHighlight();
            this._openManuscript();
          }
        });

        // "NEW" tag for unread collected manuscripts
        let newTag = null;
        if (row.isNew) {
          newTag = this.add.text(
            label.x + label.width + 8, y + 2,
            'NEW', NEW_TAG_STYLE,
          );
          this._listContainer.add(newTag);
        }

        this._rowTexts.push({
          text: label, type: 'manuscript', id: row.id,
          collected: row.collected, isNew: row.isNew, newTag,
        });
        y += ROW_HEIGHT;
      }
    }

    this._listContentHeight = y - LIST_START_Y;

    // Highlight initial cursor
    this._updateCursorHighlight();
  }

  // ---------------------------------------------------------------
  // Cursor & Selection
  // ---------------------------------------------------------------

  /** @private */
  _updateCursorHighlight() {
    if (this._selectableIndices.length === 0) return;

    // Reset all manuscript row styles
    for (const rt of this._rowTexts) {
      if (rt.type !== 'manuscript') continue;
      if (rt.isNew) {
        rt.text.setStyle(ITEM_NEW_STYLE);
      } else if (rt.collected) {
        rt.text.setStyle(ITEM_COLLECTED_STYLE);
      } else {
        rt.text.setStyle(ITEM_LOCKED_STYLE);
      }
    }

    // Highlight selected row
    const rowIdx = this._selectableIndices[this._cursorIdx];
    const rowText = this._rowTexts[rowIdx];
    if (rowText && rowText.isNew) {
      rowText.text.setStyle(ITEM_NEW_SELECTED_STYLE);
    } else if (rowText && rowText.collected) {
      rowText.text.setStyle(ITEM_SELECTED_STYLE);
    } else if (rowText) {
      // Even locked items get a slight highlight when selected
      rowText.text.setStyle({
        ...ITEM_LOCKED_STYLE,
        color: '#888888',
      });
    }

    // Ensure the selected row is visible (auto-scroll)
    this._ensureVisible(rowIdx);
  }

  /** @private */
  _ensureVisible(rowIdx) {
    // Calculate the Y position of this row relative to list start
    let yPos = 0;
    for (let i = 0; i < rowIdx; i++) {
      yPos += (this._rows[i].type === 'arc') ? ROW_HEIGHT + 2 : ROW_HEIGHT;
    }

    // When scrolling up, also show the preceding arc header if this row
    // is the first manuscript in an arc (so arc titles are never clipped)
    let yTop = yPos;
    if (rowIdx > 0 && this._rows[rowIdx - 1].type === 'arc') {
      yTop -= (ROW_HEIGHT + 2);
    }

    const viewHeight = GAME_HEIGHT - LIST_START_Y - BORDER_MARGIN - 25;
    const maxScroll = Math.max(0, this._listContentHeight - viewHeight);

    // If the row (or its arc header) is above the visible area, scroll up
    if (yTop < this._scrollOffset) {
      this._scrollOffset = Math.max(0, yTop);
    }
    // If the row is below the visible area, scroll down
    else if (yPos + ROW_HEIGHT > this._scrollOffset + viewHeight) {
      this._scrollOffset = Math.min(maxScroll, yPos + ROW_HEIGHT - viewHeight + 4);
    }

    this._listContainer.setY(-this._scrollOffset);
  }

  /** @private */
  _moveCursor(delta) {
    if (this._selectableIndices.length === 0) return;
    const prev = this._cursorIdx;
    this._cursorIdx = Phaser.Math.Clamp(
      this._cursorIdx + delta,
      0,
      this._selectableIndices.length - 1,
    );
    if (this._cursorIdx !== prev) {
      try {
        this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.3) });
      } catch (_) { /* audio fallback */ }
    }
    this._updateCursorHighlight();
  }

  // ---------------------------------------------------------------
  // Reading View
  // ---------------------------------------------------------------

  /** @private */
  _openManuscript() {
    if (this._selectableIndices.length === 0) return;
    const rowIdx = this._selectableIndices[this._cursorIdx];
    const row = this._rows[rowIdx];
    if (!row || !row.collected) {
      // Play deny sound for locked manuscripts
      try {
        this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.3) });
      } catch (_) { /* audio fallback */ }
      return;
    }

    // Find the full manuscript data
    const manuscript = MANUSCRIPTS.find((m) => m.id === row.id);
    if (!manuscript) return;

    // Play open sound
    try {
      this.sound.play(AUDIO_MANUSCRIPT_OPEN, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Mark manuscript as read — remove "new" highlight
    markManuscriptRead(row.id);
    row.isNew = false;
    const rowText = this._rowTexts[rowIdx];
    if (rowText) {
      rowText.isNew = false;
      if (rowText.newTag) {
        rowText.newTag.destroy();
        rowText.newTag = null;
      }
    }

    this._reading = true;

    // Hide list
    if (this._listContainer) this._listContainer.setVisible(false);
    if (this._fixedUI) this._fixedUI.setVisible(false);

    // Create reading view
    this._readContainer = this.add.container(0, 0);
    this._readScrollY = 0;

    // Mask
    const maskPad = BORDER_MARGIN + 8;
    const maskShape = this.make.graphics({ add: false });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(maskPad, maskPad,
      GAME_WIDTH - maskPad * 2, GAME_HEIGHT - maskPad * 2);
    this._readContainer.setMask(maskShape.createGeometryMask());

    let y = 50;

    // Arc name
    const arcName = ARC_NAMES[manuscript.arc] || `Arc ${manuscript.arc}`;
    const arcText = this.add.text(CX, y, arcName, READ_ARC_STYLE).setOrigin(0.5);
    this._readContainer.add(arcText);
    y += 22;

    // Title
    const romanNum = this._toRoman(manuscript.id);
    const titleText = this.add.text(CX, y, `${romanNum}. ${manuscript.title}`, READ_TITLE_STYLE)
      .setOrigin(0.5);
    this._readContainer.add(titleText);
    y += 28;

    // Decorative line
    const lineG = this.add.graphics();
    lineG.lineStyle(1, COLOR_WHITE, 0.2);
    lineG.lineBetween(CX - 100, y, CX + 100, y);
    this._readContainer.add(lineG);
    y += 16;

    // Body text
    const bodyText = this.add.text(CONTENT_LEFT + 10, y, manuscript.body, READ_BODY_STYLE);
    this._readContainer.add(bodyText);
    y += bodyText.height + 30;

    // Return hint (clickable)
    const hint = this.add.text(CX, y, 'ESC / click to return', READ_HINT_STYLE).setOrigin(0.5);
    this._readContainer.add(hint);
    hint.setInteractive({ useHandCursor: true });
    hint.on('pointerdown', () => {
      this._closeReading();
    });
    this.tweens.add({
      targets: hint,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
    y += 30;

    this._readContentHeight = y;
    this._readMaxScroll = Math.max(0, this._readContentHeight - (GAME_HEIGHT - 20));
  }

  /** @private */
  _closeReading() {
    if (!this._reading) return;
    this._reading = false;

    try {
      this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.4) });
    } catch (_) { /* audio fallback */ }

    // Destroy reading view
    if (this._readContainer) {
      this._readContainer.destroy();
      this._readContainer = null;
    }

    // Show list
    if (this._listContainer) this._listContainer.setVisible(true);
    if (this._fixedUI) this._fixedUI.setVisible(true);
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.input.keyboard.on('keydown-ESC', () => {
      if (this._reading) {
        this._closeReading();
      } else {
        try {
          this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.5) });
        } catch (_) { /* audio fallback */ }
        this._returnToTitle();
      }
    });

    this.input.keyboard.on('keydown-ENTER', () => {
      if (this._reading) {
        this._closeReading();
      } else {
        this._openManuscript();
      }
    });

    this.input.keyboard.on('keydown-SPACE', () => {
      if (this._reading) {
        this._closeReading();
      } else {
        this._openManuscript();
      }
    });

    this.input.keyboard.on('keydown-UP', () => {
      if (this._reading) {
        this._scrollReading(-30);
      } else {
        this._moveCursor(-1);
      }
    });

    this.input.keyboard.on('keydown-DOWN', () => {
      if (this._reading) {
        this._scrollReading(30);
      } else {
        this._moveCursor(1);
      }
    });

    // Mouse wheel
    this.input.on('wheel', (_pointer, _gos, _dx, dy) => {
      if (this._reading) {
        this._scrollReading(dy > 0 ? 30 : -30);
      } else {
        this._moveCursor(dy > 0 ? 1 : -1);
      }
    });
  }

  /** @private */
  _scrollReading(delta) {
    if (!this._readContainer || this._readMaxScroll <= 0) return;
    this._readScrollY = Phaser.Math.Clamp(
      this._readScrollY + delta,
      0,
      this._readMaxScroll,
    );
    this._readContainer.setY(-this._readScrollY);
  }

  /** @private Start library background music if music preference is on. */
  _startMusic() {
    const musicOn = loadMusicPref();
    if (!musicOn) return;
    try {
      this._libraryMusic = this.sound.add(AUDIO_LIBRARY_BG, {
        loop: true,
        volume: getMusicVol(0.4),
      });
      this._libraryMusic.play();
    } catch (_e) {
      // Graceful fallback if audio file is missing
      this._libraryMusic = null;
    }
  }

  /** @private Stop library background music. */
  _stopMusic() {
    if (this._libraryMusic && this._libraryMusic.isPlaying) {
      this._libraryMusic.stop();
      this._libraryMusic = null;
    }
  }

  /** @private */
  _returnToTitle() {
    if (this._transitioning) return;
    this._transitioning = true;
    this._stopMusic();
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }

  // ---------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------

  /**
   * Convert a number (1-30) to a Roman numeral string.
   * @private
   * @param {number} num
   * @returns {string}
   */
  _toRoman(num) {
    const lookup = [
      [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
      [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
      [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
    ];
    let result = '';
    let remaining = num;
    for (const [value, symbol] of lookup) {
      while (remaining >= value) {
        result += symbol;
        remaining -= value;
      }
    }
    return result;
  }

  // ---------------------------------------------------------------
  // Decorative Border
  // ---------------------------------------------------------------

  /** @private */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }
}
